/**
 * ApprovalsPanel — embedded panel for the WorkflowsPage "Approvals" tab.
 *
 * Layout mirrors ExecutionHistoryPanel exactly:
 *  - Same sidebar navigation (Owned by me / All approvals)
 *  - Same Backstage Table component with pagination, search, and dense rows
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';

import LaunchIcon from '@mui/icons-material/Launch';
import { Refresh as RefreshIcon, Search as SearchIcon, Settings as SettingsIcon, ViewList as ViewListIcon } from '@mui/icons-material';

import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Table, TableColumn, Link } from '@backstage/core-components';

import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import type { ToolchainApproval } from '../../types';

// ---------------------------------------------------------------------------
// Styles — mirrors ExecutionHistoryPanel in WorkflowsPage
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  historyPaper: {
    '& .MuiPaper-root': { borderRadius: `${4}px !important` },
    '& .MuiCard-root': { borderRadius: `${4}px !important` },
  },
  layout: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    position: 'sticky' as const,
    top: 0,
  },
  sidebarLabel: {
    display: 'block',
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
  },
  sidebarList: { padding: theme.spacing(0, 0, 1) },
  navItem: {
    width: 'auto',
    borderRadius: 4,
    margin: theme.spacing(0.2, 1),
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 0.12s',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  navItemActive: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.18)' : '#FDF0F8' },
  navIconActive: { color: theme.palette.primary.main, fontSize: 18 },
  navIconInactive: { color: theme.palette.text.secondary, fontSize: 18 },
  navTextActive: { color: theme.palette.primary.main, fontWeight: 700 },
  execId: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  waitingFor: {
    fontWeight: 600,
    color: (theme.palette as any).warning?.main ?? '#f47920',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '2px 8px',
    height: 40,
    gap: 4,
    backgroundColor: theme.palette.background.paper,
  },
}));

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function formatRelative(tsMs: number): string {
  const diff = Date.now() - tsMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatDuration(isoString: string | null, fallbackMs: number): string {
  const startMs = isoString ? new Date(isoString).getTime() : fallbackMs;
  const diff = Date.now() - startMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function formatDateTime(tsMs: number): string {
  return new Date(tsMs).toLocaleString();
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

type ApprovalsFilter = 'mine' | 'all';

export function ApprovalsPanel() {
  const { classes } = useStyles();
  const api = useApi(oneadvancedApiRef);
  const identityApi = useApi(identityApiRef);

  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get('filter') as ApprovalsFilter) ?? 'mine';
  const [approvals, setApprovals] = useState<ToolchainApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [currentUserRef, setCurrentUserRef] = useState<string | null>(null);
  const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const setFilter = (v: ApprovalsFilter) => {
    setWorkflowFilter(null);
    setSearchText('');
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('filter', v); return next; }, { replace: true });
  };

  const loadApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.toolchainApprovals.getPendingApprovals();
      setApprovals(result.data);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      setCurrentUserEmail(profile.email ?? null);
    });
    identityApi.getBackstageIdentity().then(identity => {
      setCurrentUserRef(identity.userEntityRef ?? null);
    });
  }, [identityApi]);

  useEffect(() => { loadApprovals(); }, [loadApprovals]);

  const myApprovals = useMemo(() => {
    if (!currentUserEmail && !currentUserRef) return [];
    return approvals.filter(a =>
      (currentUserRef && a.requesterRef === currentUserRef) ||
      (currentUserEmail && a.requesterEmail === currentUserEmail),
    );
  }, [approvals, currentUserEmail, currentUserRef]);

  // Unique workflow names for the dropdown, derived from the owner-filtered set
  const workflowOptions = useMemo(() => {
    const base = filter === 'mine' ? myApprovals : approvals;
    const seen = new Set<string>();
    const options: string[] = [];
    for (const a of base) {
      const label = a.templateTitle ?? a.pipelineName;
      if (!seen.has(label)) { seen.add(label); options.push(label); }
    }
    return options.sort();
  }, [filter, myApprovals, approvals]);

  // Oldest first — highest urgency (closest to expiry) at the top
  // Applies workflow dropdown and text search filters
  const visibleApprovals = useMemo(() => {
    const base = filter === 'mine' ? myApprovals : approvals;
    const workflowFiltered = workflowFilter
      ? base.filter(a => (a.templateTitle ?? a.pipelineName) === workflowFilter)
      : base;
    const q = searchText.trim().toLowerCase();
    const filtered = q
      ? workflowFiltered.filter(a =>
          (a.backstageTaskId ?? '').toLowerCase().includes(q) ||
          (a.templateTitle ?? a.pipelineName).toLowerCase().includes(q) ||
          (a.requesterEmail ?? '').toLowerCase().includes(q)
        )
      : workflowFiltered;
    return [...filtered].sort((a, b) => a.startTs - b.startTs);
  }, [filter, myApprovals, approvals, workflowFilter, searchText]);

  // Toolbar override — renders [title] [spacer] [workflow dropdown] [search] [refresh]
  // components.Actions always renders before the search box so we own the full toolbar
  // to achieve the correct left-to-right order.
  const tableComponents = useMemo(() => ({
    Toolbar: () => (
      <Box display="flex" alignItems="center" px={2} style={{ minHeight: 56, gap: 8 }}>
        <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700, flex: '0 0 auto', marginRight: 8 }}>
          Pending Approvals
        </Typography>
        <Box flex={1} />
        <Autocomplete<string>
          options={workflowOptions}
          value={workflowFilter}
          onChange={(_e, val) => setWorkflowFilter(val)}
          style={{ width: 240 }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="All workflows"
              variant="outlined"
              size="small"
            />
          )}
        />
        <Box className={classes.searchBox}>
          <SearchIcon style={{ fontSize: 18, opacity: 0.5 }} />
          <InputBase
            placeholder="Search"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ fontSize: 14, width: 160 }}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => loadApprovals()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }), [workflowOptions, workflowFilter, searchText, loadApprovals, classes]);

  const sidebarItems: { id: ApprovalsFilter; label: string; Icon: React.ComponentType<any> }[] = [
    { id: 'mine', label: 'Owned by me', Icon: SettingsIcon },
    { id: 'all', label: 'All approvals', Icon: ViewListIcon },
  ];

  const baseColumns: TableColumn<ToolchainApproval>[] = [
    {
      title: 'Execution ID',
      field: 'backstageTaskId',
      highlight: true,
      render: row => {
        const taskId = row.backstageTaskId;
        if (!taskId) return <span style={{ opacity: 0.4, fontFamily: 'monospace', fontSize: 12 }}>—</span>;
        return (
          <Link to={`/create/tasks/${taskId}`} className={classes.execId}>
            {taskId}
          </Link>
        );
      },
    },
    {
      title: 'Workflow',
      field: 'templateTitle',
      render: row => row.templateTitle ?? row.pipelineName,
    },
    {
      title: 'Created',
      field: 'startTs',
      render: row => (
        <Tooltip title={formatDateTime(row.startTs)}>
          <span>{formatRelative(row.startTs)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Approval Waiting For',
      field: 'fetchedAt',
      render: row => (
        <span className={classes.waitingFor}>
          {formatDuration(row.fetchedAt, row.startTs)}
        </span>
      ),
    },
  ];

  const allColumns: TableColumn<ToolchainApproval>[] = [
    ...baseColumns,
    {
      title: 'Execution Owner',
      field: 'requesterEmail',
      render: row => {
        const display = row.requesterRef
          ? row.requesterRef.replace(/^[^:]+:[^/]+\//, '')
          : row.requesterEmail ?? null;
        return display ? (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <span style={{ fontSize: 12 }}>{display}</span>
          </Box>
        ) : (
          <span style={{ opacity: 0.4, fontSize: 12 }}>Unknown</span>
        );
      },
    },
    {
      title: 'Action',
      field: 'pipelineUrl',
      sorting: false,
      render: row => (
        <Button
          size="small"
          variant="contained"
          color="primary"
          endIcon={<LaunchIcon style={{ fontSize: 13 }} />}
          href={row.pipelineUrl}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
        >
          Open to Approve
        </Button>
      ),
    },
  ];

  const columns = filter === 'mine' ? baseColumns : allColumns;

  return (
    <div className={classes.historyPaper}>
      <div className={classes.layout}>
        {/* Sidebar */}
        <Paper className={classes.sidebar} elevation={0}>
          <Typography component="span" className={classes.sidebarLabel}>
            Approval Owner
          </Typography>
          <List disablePadding className={classes.sidebarList}>
            {sidebarItems.map(({ id, label, Icon }) => {
              const active = filter === id;
              return (
                <div
                  key={id}
                  className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setFilter(id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setFilter(id); }}
                >
                  <Icon className={active ? classes.navIconActive : classes.navIconInactive} />
                  <Typography
                    component="span"
                    className={active ? classes.navTextActive : undefined}
                    style={{ fontSize: 13, fontWeight: active ? 700 : 500, flex: 1 }}
                  >
                    {label}
                  </Typography>
                </div>
              );
            })}
          </List>
        </Paper>

        {/* Table */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Table<ToolchainApproval>
            title="Pending Approvals"
            isLoading={loading}
            data={visibleApprovals}
            columns={columns}
            components={tableComponents}
            options={{
              paging: true,
              pageSize: 5,
              pageSizeOptions: [5, 10, 20, 50],
              emptyRowsWhenPaging: false,
              padding: 'dense',
              search: false,
              toolbar: true,
            }}
            emptyContent={
              <Box display="flex" flexDirection="column" alignItems="center" p={4} style={{ opacity: 0.5, gap: 8 }}>
                <Typography variant="body1" style={{ fontWeight: 700 }}>
                  {filter === 'mine' ? 'You have no pending approval requests.' : 'No pipeline executions are currently waiting for approval.'}
                </Typography>
              </Box>
            }
          />
        </div>
      </div>
    </div>
  );
}
